import { ModelRow } from "@mcleod/core";
import { ClickEvent, MapPin, SelectionType, TableRow, NoMap } from "@mcleod/components";
import { MovementMap } from "@mcleod/dispatch";
import { RecentCallins } from "./RecentCallins";
import { AutogenLayoutMovementCallins } from "./autogen/AutogenLayoutMovementCallins";

const rowClickZoomLevel = 10;

export class MovementCallins extends AutogenLayoutMovementCallins {
    private _movementMap: MovementMap;
    private _movementId;

    set movementMap(value: MovementMap) {
        this._movementMap = value;
        this._movementMap.mapPinOnClick = (e: ClickEvent, pin: MapPin) => this.mapPinOnClick(e, pin);
    }

    get movementMap(): MovementMap {
        return this._movementMap;
    }

    override onLoad() {
        this.recentCallinLayout.newestFirst = false;
        this.recentCallinLayout.callinRowCallback = (row: ModelRow) => {
            if (!this.isMapCreated())
                return;

            const callinId = row.get("id");
            const callinLatitude = row.get("latitude");
            const callinLongitude = row.get("longitude");
            this.movementMap.mapStatus.setCenter(callinLatitude, Math.abs(callinLongitude));
            this.movementMap.mapStatus.setZoom(rowClickZoomLevel);
            const pins = this.movementMap.mapStatus.pins;
            let index = pins.findIndex(pin => pin.data?.get("callin_id") === callinId);
            if (index === -1) {
                let tempCallinLongitude = callinLongitude;
                if (callinLongitude > 0) {
                    tempCallinLongitude *= -1;
                }
                index = pins.findIndex(pin => pin.latitude === callinLatitude && pin.longitude === tempCallinLongitude);
            }
            if(index !== -1) {
                this.movementMap.changeMapPinFocus(index);
            }
        }
    }

    private isMapCreated() {
        return !(this.movementMap.mapStatus == null ||
            this.movementMap.mapStatus.map instanceof NoMap);
    }

    private get recentCallinLayout(): RecentCallins {
        return this.layoutRecentCallins;
    }

    public set movementId(value: string) {
        if (value != this._movementId) {
            this._movementId = value;
            this.searchCallinsAndMap();
        }
    }

    public get movementId(): string {
        return this._movementId;
    }

    private searchCallinsAndMap() {
        if (this._movementId != null) {
            this.recentCallinLayout.includePositions = this.switchSwitchPositions.checked;

            if (this.recentCallinLayout.mainDataSource == null)
                this.recentCallinLayout.addLayoutDataLoadListener(() => this.recentCallinLayout.doSearch(this.movementId));
            else
                this.recentCallinLayout.doSearch(this.movementId)

            this.movementMap.searchMap(this.movementId, this.switchSwitchPositions.checked);
        }
    }

    switchSwitchPositionsOnClick(event: ClickEvent) {
        this.searchCallinsAndMap();
    }

    private mapPinOnClick(e: ClickEvent, pin: MapPin) {
        const targetPinId = pin.data?.get("callin_id");
        let callinTableRow: TableRow;

        if (targetPinId != null) {
            const listOfCallin = this.recentCallinLayout.tableCallins.rows;
            callinTableRow = listOfCallin.find(callin => callin.data.get("id") == targetPinId);
        } else if (pin.data?.get("data_type") === "tractor_position") {
            callinTableRow = this.findRowForPositionPin(pin);
        }

        if (callinTableRow == null) {
            return;
        }

        const selectedCallinID = this.recentCallinLayout.tableCallins.selectedRow?.data.get("id");
        const targetRowId = callinTableRow.data.get("id");
        if (targetRowId !== selectedCallinID) {
            this.recentCallinLayout.tableCallins.selectRow(callinTableRow, SelectionType.SINGLE, e.domEvent);
        }

        if (targetPinId != null) {
            const pinsArray = this.movementMap.mapStatus.pins;
            const targetIndex: number = pinsArray.findIndex(mapPin => mapPin.data?.get("callin_id") == targetPinId);
            if(targetIndex !== -1) {
                this.movementMap.changeMapPinFocus(targetIndex);
            }
        }
    }

    private findRowForPositionPin(pin: MapPin): TableRow {
        const matchingRows = this.recentCallinLayout.tableCallins.rows.filter(row =>
            this.coordinatesMatch(row.data.get("latitude"), row.data.get("longitude"), pin.latitude, pin.longitude)
        );

        if (matchingRows.length === 0) {
            return null;
        }

        const pinTime = this.getTimeValue(pin.data?.get("position_date"));
        if (pinTime == null) {
            return matchingRows[0];
        }

        let bestRow = matchingRows[0];
        let bestDistance = Math.abs((this.getTimeValue(bestRow.data.get("call_date_time")) ?? pinTime) - pinTime);

        for (const row of matchingRows.slice(1)) {
            const rowTime = this.getTimeValue(row.data.get("call_date_time"));
            const distance = Math.abs((rowTime ?? pinTime) - pinTime);
            if (distance < bestDistance) {
                bestRow = row;
                bestDistance = distance;
            }
        }

        return bestRow;
    }

    private coordinatesMatch(rowLatitude: number, rowLongitude: number, pinLatitude: number, pinLongitude: number): boolean {
        return Number(rowLatitude) === Number(pinLatitude)
            && this.normalizeLongitude(rowLongitude) === this.normalizeLongitude(pinLongitude);
    }

    private normalizeLongitude(longitude: number): number {
        const numericLongitude = Number(longitude);
        return numericLongitude > 0 ? numericLongitude * -1 : numericLongitude;
    }

    private getTimeValue(value: Date | string): number {
        if (value == null) {
            return null;
        }

        const date = value instanceof Date ? value : new Date(value);
        const timeValue = date.getTime();
        return Number.isNaN(timeValue) ? null : timeValue;
    }

    /** This is an event handler for the afterExecution event of sourceMovement.  */
    sourceMovementAfterExecution(event) {
        if (event?.getAction() == "search" && this.activeRow?.get("brokerage", "N") === "Y") {
            this.movementId = this.activeRow?.get("id");
        }
    }

}
